import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AssistantToolCall, AssistantPendingAction } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

/**
 * Driver EVII chat state — the conversation lifted OUT of DriverAssistSheet
 * so it survives navigation. Mirrors the admin store (apps/admin/src/store/
 * evie.ts): view/open state in memory only, a per-user capped-3 recent
 * memory persisted to localStorage keyed by user._id, and NO data access —
 * presentation and conversation state only.
 *
 *   1. open        — whether the (single, AppShell-mounted) sheet is showing.
 *                    In-memory: EVII is closed on every load.
 *   2. context     — charge-surface binding set by PreSession/Charging while
 *                    they are mounted (surface copy, session/charger ids, the
 *                    prefilled support path). Cleared on their unmount.
 *   3. conversation — id + transcript for the ACTIVE thread. In-memory: it
 *                    survives route changes (the sheet no longer unmounts)
 *                    but not a reload. The server keeps its own copy keyed
 *                    on conversation_id for 1h; continuing an expired id
 *                    simply starts fresh server-side context.
 *   4. conversationsByUser — the last MAX_RECENT_CONVERSATIONS transcripts
 *                    per user, persisted. Same cap the admin memory uses
 *                    (MAX_RECENT_INQUIRIES = 3), lifted from Q&A pairs to
 *                    whole conversations so the history panel can resume one.
 */

/** One transcript entry. Moved verbatim from DriverAssistSheet so the store
 *  can hold the thread; the sheet re-imports it from here. */
export type MessageEntry =
  | {
      kind: 'user';
      text: string;
      timestamp: number;
    }
  | {
      kind: 'assistant';
      text: string;
      timestamp: number;
      tool_calls: AssistantToolCall[];
      suggested_action:
        | 'contact_support'
        | 'soft_reset_fired'
        | 'hard_reset_fired'
        | null;
      /** Set when EVII prepared a receipt — drives the Download receipt
       *  button. The range is passed straight to the PDF endpoint. */
      receipt_download?: { from: string; to: string } | null;
      /** Set when EVII offered a destination — drives the navigation button.
       *  The path is server-allowlisted; tapping routes there. */
      navigation?: { route: string; path: string; label: string } | null;
      /** Set when EVII PROPOSED an action needing confirmation (report_problem)
       *  — drives the in-chat confirm card. Cleared once confirmed/cancelled. */
      pending_action?: AssistantPendingAction | null;
    }
  | {
      kind: 'error';
      text: string;
      timestamp: number;
      retryable: boolean;
    };

export type EvieSurface = 'charge-start' | 'general' | 'store';

/** A resumable past conversation in the capped per-user memory. */
export interface StoredEvieConversation {
  /** The conversation_id — reused on resume so server context (if still
   *  within its 1h TTL) continues seamlessly. */
  id: string;
  /** Full transcript snapshot at last activity. */
  messages: MessageEntry[];
  /** First driver message — the history row preview and the seed for the
   *  suggested-prompt heuristic. */
  first_user_text: string;
  /** Last-activity epoch ms, for relative time + newest-first order. */
  at: number;
}

/** Warren's cap, same as the admin memory: last 3 only. */
export const MAX_RECENT_CONVERSATIONS = 3;

function mintConversationId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Snapshot cap per conversation: the server keeps 24 messages; 40 gives
 *  headroom for error/confirm entries without letting a marathon thread
 *  bloat the persisted localStorage blob. */
const SNAPSHOT_MESSAGE_CAP = 40;

/** The binding a conversation was started under. The SERVER binds
 *  session/charger at conversation creation and ignores them afterward,
 *  so a thread minted on the map must not carry into a charge screen
 *  (its status/reset tools would target nothing or the wrong charger). */
function contextKeyOf(s: { surface: EvieSurface; sessionId: string | null; chargerId: string | null }): string {
  return s.surface === 'charge-start'
    ? `charge:${s.sessionId ?? ''}:${s.chargerId ?? ''}`
    : 'general';
}

/** Pure snapshot upsert used by recordSnapshot and the open-time rotation. */
function snapshotInto(
  byUser: Record<string, StoredEvieConversation[]>,
  userId: string | null,
  conversationId: string,
  messages: MessageEntry[],
): Record<string, StoredEvieConversation[]> {
  if (!userId) return byUser;
  const firstUser = messages.find((m) => m.kind === 'user');
  if (!firstUser) return byUser;
  const snap: StoredEvieConversation = {
    id: conversationId,
    messages: messages.slice(-SNAPSHOT_MESSAGE_CAP),
    first_user_text: firstUser.text,
    at: Date.now(),
  };
  const others = (byUser[userId] ?? []).filter((c) => c.id !== snap.id);
  return { ...byUser, [userId]: [snap, ...others].slice(0, MAX_RECENT_CONVERSATIONS) };
}

interface EvieChatState {
  open: boolean;
  openSheet: () => void;
  closeSheet: () => void;

  surface: EvieSurface;
  sessionId: string | null;
  chargerId: string | null;
  /** Where the contact-support CTA routes; charge pages prefill params. */
  supportPath: string;
  setContext: (ctx: {
    surface: EvieSurface;
    sessionId?: string | null;
    chargerId?: string | null;
    supportPath?: string;
  }) => void;
  /** Back to the general surface. Called on charge-page unmount. */
  clearContext: () => void;

  conversationId: string;
  messages: MessageEntry[];
  /** The context key the active conversation was started under (see
   *  contextKeyOf). openSheet rotates to a fresh conversation when the
   *  current context no longer matches, so charge-screen threads always
   *  carry the right server-side binding. */
  conversationContextKey: string;
  setMessages: (updater: MessageEntry[] | ((prev: MessageEntry[]) => MessageEntry[])) => void;
  /** Drop the active thread and context (fresh id, closed sheet). Fired
   *  on auth-user change so a shared device never shows the previous
   *  account's transcript. The persisted per-user memory is untouched —
   *  it is keyed by user id and only ever read for the signed-in user. */
  resetActive: () => void;

  /** Snapshot the active conversation into the caller's capped memory.
   *  Called after each assistant reply lands (the admin pattern records
   *  after every successful send). Greeting-only threads are skipped. */
  recordSnapshot: (userId: string | null) => void;
  /** Load a stored conversation as the active thread (history panel tap).
   *  Does NOT touch voice state — the mic follows the sheet-open gate. */
  activateConversation: (userId: string | null, id: string) => void;

  conversationsByUser: Record<string, StoredEvieConversation[]>;
}

export const useEvieChatStore = create<EvieChatState>()(
  persist(
    (set) => ({
      open: false,
      openSheet: () => set((s) => {
        const key = contextKeyOf(s);
        // Adopt the current context on a fresh/greet-only thread; rotate
        // to a new conversation when a real thread was started under a
        // DIFFERENT binding (snapshotting it into the per-user memory
        // first, so nothing is lost — it stays resumable from history).
        const hasRealThread = s.messages.some((m) => m.kind === 'user');
        if (!hasRealThread) {
          return { open: true, conversationContextKey: key };
        }
        if (key === s.conversationContextKey) {
          return { open: true };
        }
        const userId = useAuthStore.getState().user?._id ?? null;
        return {
          open: true,
          conversationId: mintConversationId(),
          messages: [],
          conversationContextKey: key,
          conversationsByUser: snapshotInto(
            s.conversationsByUser, userId, s.conversationId, s.messages,
          ),
        };
      }),
      closeSheet: () => set({ open: false }),

      surface: 'general',
      sessionId: null,
      chargerId: null,
      supportPath: '/support',
      setContext: (ctx) => set({
        surface: ctx.surface,
        sessionId: ctx.sessionId ?? null,
        chargerId: ctx.chargerId ?? null,
        supportPath: ctx.supportPath ?? '/support',
      }),
      clearContext: () => set({
        surface: 'general',
        sessionId: null,
        chargerId: null,
        supportPath: '/support',
      }),

      conversationId: mintConversationId(),
      messages: [],
      conversationContextKey: 'general',
      setMessages: (updater) => set((s) => ({
        messages: typeof updater === 'function' ? updater(s.messages) : updater,
      })),
      resetActive: () => set({
        open: false,
        conversationId: mintConversationId(),
        messages: [],
        conversationContextKey: 'general',
        surface: 'general',
        sessionId: null,
        chargerId: null,
        supportPath: '/support',
      }),

      recordSnapshot: (userId) => set((s) => ({
        conversationsByUser: snapshotInto(
          s.conversationsByUser, userId, s.conversationId, s.messages,
        ),
      })),

      activateConversation: (userId, id) => set((s) => {
        const stored = userId
          ? (s.conversationsByUser[userId] ?? []).find((c) => c.id === id)
          : undefined;
        if (!stored) return {};
        return {
          conversationId: stored.id,
          // Strip any stored confirm cards: their server tokens are stale
          // (or worse, still live out of context) — never resurrect one.
          messages: stored.messages.map((m) =>
            m.kind === 'assistant' && m.pending_action ? { ...m, pending_action: null } : m,
          ),
          // A resumed thread keeps its original binding; mark it with the
          // CURRENT context key so openSheet doesn't immediately rotate it.
          conversationContextKey: contextKeyOf(s),
        };
      }),

      conversationsByUser: {},
    }),
    {
      name: 'evi-web-evie-chat',
      // Persist ONLY the capped per-user memory. open/context/active thread
      // stay in-memory so a reload opens fresh (matching the admin store's
      // partialize discipline).
      partialize: (s) => ({ conversationsByUser: s.conversationsByUser }),
    },
  ),
);

// Auth-change guard: when the signed-in user changes (logout, token-refresh
// clearAuth, account switch in another tab), drop the active thread so the
// next account never sees or extends the previous one. Import direction is
// evieChat -> auth only, so no module cycle; the subscription reads lazily.
let lastEvieUserId: string | null = useAuthStore.getState().user?._id ?? null;
useAuthStore.subscribe((s) => {
  const uid = s.user?._id ?? null;
  if (uid !== lastEvieUserId) {
    lastEvieUserId = uid;
    useEvieChatStore.getState().resetActive();
  }
});
